import * as THREE from 'three';
import {SketchTrimMode, type SketchTrimCurveObject} from '../SketchTrimTypes';
import type {SketchTrimModeService} from './SketchTrimModeServiceTypes';
import type {SketchTrimController} from '../SketchTrimController';

/**
 * TrimToClosest 模式服务。
 *
 * 职责：
 * 1. 消费选择管理器的单曲线选择结果；
 * 2. 将命中点写回最近端工作流；
 * 3. 统一处理修剪与延伸结果提示。
 */
export class SketchTrimToClosestModeService implements SketchTrimModeService {
    /** 当前服务绑定的模式。 */
    public readonly mode = SketchTrimMode.TrimToClosest;
    /** 所属剪裁控制器。 */
    private readonly controller: SketchTrimController;

    /**
     * 构造函数。
     * @param controller 剪裁控制器。
     */
    public constructor(controller: SketchTrimController) {
        this.controller = controller;
    }

    /**
     * 获取模式显示文案。
     * @returns 文案。
     */
    public getDisplayName(): string {
        return '剪裁到最近端（Trim To Closest）';
    }

    /**
     * 重置模式私有状态。
     * @param context 服务上下文。
     */
    public resetState(): void {
        this.controller.serviceSetLastSelectedEntity(null);
    }

    /**
     * 判断当前行为是否允许选择目标实体。
     *
     * 剪裁行为遵循通用 Trim 曲线矩阵；延伸行为保持现有直线能力，避免把
     * “可剪裁曲线”错误扩大成“可延伸曲线”。
     *
     * @param object 待判断场景对象。
     * @returns 当前 TrimToClosest 行为允许选择时返回 true。
     */
    public isSelectableEntity(object: THREE.Object3D): boolean {
        return this.controller.serviceIsTrimToClosestSelectableEntity(object);
    }

    /**
     * 处理选择管理器结算。
     * @param context 服务上下文。
     */
    public handleSelectionUpdated(): void {
        const selectionManager = this.controller.getViewer().entitySelectionManager;
        if (!selectionManager) {
            return;
        }

        const selectedCurvePicks = selectionManager.getSelectedPicks()
            .filter((pick): pick is THREE.Intersection<SketchTrimCurveObject> => (
                this.isSelectableEntity(pick.object)
            ));
        if (selectedCurvePicks.length <= 0) {
            this.controller.serviceSetLastSelectedEntity(null);
            return;
        }

        const targetEntity = this.controller.serviceCreateTrimEntityRefFromIntersection(
            selectedCurvePicks[selectedCurvePicks.length - 1]!,
        );
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(targetEntity));
        const result = this.controller.serviceApplyTrimToClosestOperation(targetEntity);
        this.controller.serviceClearTrimSelectionFromSelectionManager();
        this.controller.servicePublishResult(result);
    }
}
